import React from 'react';

/**
 * Catches the "failed to load a lazily-imported chunk" error that can happen
 * when a user has the SPA open in a tab, we deploy (which replaces the hashed
 * chunk files on S3 with `--delete`), and they then navigate to a route whose
 * old chunk no longer exists. The fix is simply to reload: index.html is served
 * `no-cache`, so a fresh load pulls the current index.html → current chunk
 * names. We guard with a sessionStorage flag so a genuinely broken chunk (not a
 * stale one) doesn't spin in a reload loop.
 *
 * Donor-facing routes are kept as eager imports (not lazy), so this can only
 * ever trigger for logged-in users navigating the app — never on the public
 * donation flow.
 */
const RELOAD_FLAG = 'chunk-reload-attempted';

function isChunkLoadError(err: unknown): boolean {
  const msg = (err as any)?.message || '';
  const name = (err as any)?.name || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

interface State {
  error: Error | null;
}

class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      // First time we see a stale-chunk error this session: reload once to pull
      // the fresh build. The flag prevents an infinite reload loop if the chunk
      // is genuinely missing rather than just stale.
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.error && isChunkLoadError(this.state.error) && sessionStorage.getItem(RELOAD_FLAG)) {
      // We already reloaded once and still failed — show a manual retry instead
      // of looping. Clearing the flag lets the button try a fresh reload.
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4 text-center p-6">
          <p className="text-gray-700">העמוד עודכן. רעננו כדי לטעון את הגרסה החדשה.</p>
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white"
            onClick={() => { sessionStorage.removeItem(RELOAD_FLAG); window.location.reload(); }}
          >
            רענון
          </button>
        </div>
      );
    }
    if (this.state.error && !isChunkLoadError(this.state.error)) {
      // Non-chunk render errors: rethrow so we don't silently swallow real bugs.
      throw this.state.error;
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
